import { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import angelOneApi from '../services/angelOneApi';
import { SENSEX_CONFIG, CANDLE_INTERVALS } from '../config/angelOne';

const functions = getFunctions();
const algoScan = httpsCallable(functions, 'sensexAlgoScan');
const algoRecordResult = httpsCallable(functions, 'sensexAlgoRecordResult');
const algoResetDaily = httpsCallable(functions, 'sensexAlgoResetDaily');
const algoUpdateSettings = httpsCallable(functions, 'sensexAlgoUpdateSettings');
const algoStats = httpsCallable(functions, 'sensexAlgoStats');
const algoMonteCarlo = httpsCallable(functions, 'sensexAlgoMonteCarlo');
const algoSystemHealth = httpsCallable(functions, 'sensexAlgoSystemHealth');
const algoPerformance = httpsCallable(functions, 'sensexAlgoPerformance');

export default function useSensexAlgo({ capital = 50000, mode = 'MANUAL', enabled = false }) {
  const [signals, setSignals] = useState([]);
  const [regime, setRegime] = useState(null);
  const [expectedMove, setExpectedMove] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [moveExhausted, setMoveExhausted] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [stats, setStats] = useState(null);
  const [marketOpen, setMarketOpen] = useState(true);
  // Advanced state
  const [dayType, setDayType] = useState(null);
  const [moveUtilization, setMoveUtilization] = useState(null);
  const [transition, setTransition] = useState(null);
  const [timePhase, setTimePhase] = useState(null);
  const [drawdown, setDrawdown] = useState(null);
  const [protectionZone, setProtectionZone] = useState(null);
  const [scanMs, setScanMs] = useState(null);

  const intervalRef = useRef(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Fetch candles + option chain + spot, then call algo backend
  const runScan = useCallback(async () => {
    if (!enabledRef.current) return;

    try {
      setScanning(true);
      setError(null);

      const now = new Date();
      const { fromDate, toDate, isLive } = getCandleDateRange(now);
      setMarketOpen(isLive);

      // Fetch candles and spot in parallel
      const [candleRes, spotRes] = await Promise.all([
        angelOneApi.getCandles({
          exchange: SENSEX_CONFIG.EXCHANGE,
          symboltoken: SENSEX_CONFIG.TOKEN,
          interval: CANDLE_INTERVALS.FIVE_MINUTE,
          fromdate: fromDate,
          todate: toDate,
        }),
        angelOneApi.getLTP({
          exchange: SENSEX_CONFIG.EXCHANGE,
          symboltoken: SENSEX_CONFIG.TOKEN,
          tradingsymbol: SENSEX_CONFIG.SYMBOL,
        }),
      ]);

      const candles = candleRes?.data?.data || [];
      const spot = spotRes?.data?.data?.ltp || 0;

      if (candles.length < 5) {
        setError(isLive
          ? 'Insufficient candle data — market may have just opened'
          : 'Insufficient candle data — market is closed, showing last session');
        return;
      }

      // Parse candles to standard format
      const parsedCandles = candles.map(c => ({
        time: c[0],
        open: parseFloat(c[1]),
        high: parseFloat(c[2]),
        low: parseFloat(c[3]),
        close: parseFloat(c[4]),
        volume: parseInt(c[5] || 0),
      }));

      // Try to get option chain data (best effort)
      let optionChain = [];
      let greeks = {};
      let atmCePremium = 0;
      let atmPePremium = 0;
      let timeToExpiry = 300;

      try {
        const greeksRes = await angelOneApi.getOptionGreeks({
          name: SENSEX_CONFIG.NAME,
          expirydate: getNextExpiry(),
        });
        const gData = greeksRes?.data?.data || [];

        // Find ATM strike
        const atmStrike = Math.round(spot / SENSEX_CONFIG.STRIKE_INTERVAL) * SENSEX_CONFIG.STRIKE_INTERVAL;

        gData.forEach(item => {
          optionChain.push({
            strikePrice: item.strikeprice || item.strikePrice,
            optionType: item.optiontype || item.optionType,
            ltp: item.ltp,
            tradingsymbol: item.tradingsymbol,
            symboltoken: item.symboltoken,
            oi: item.opInt || 0,
            volume: item.vol || 0,
          });
          const key = `${item.strikeprice || item.strikePrice}_${item.optiontype || item.optionType}`;
          greeks[key] = {
            delta: item.delta || 0,
            gamma: item.gamma || 0,
            theta: item.theta || 0,
            vega: item.vega || 0,
            iv: item.impliedVolatility || item.iv || 0,
          };

          // Find ATM premiums
          const sp = parseFloat(item.strikeprice || item.strikePrice || 0);
          if (sp === atmStrike) {
            if ((item.optiontype || item.optionType) === 'CE') atmCePremium = item.ltp || 0;
            if ((item.optiontype || item.optionType) === 'PE') atmPePremium = item.ltp || 0;
          }
        });

        // Calculate time to expiry
        const expiry = getNextExpiry();
        if (expiry) {
          const expiryDate = new Date(expiry + ' 15:30:00');
          timeToExpiry = Math.max(0, Math.floor((expiryDate - now) / 60000));
        }
      } catch (e) {
        console.warn('Option chain fetch failed (non-critical):', e.message);
      }

      // Determine if expiry day (Friday)
      const isExpiry = now.getDay() === 5;

      // Call Python backend via Cloud Function
      const result = await algoScan({
        candles: parsedCandles,
        option_chain: optionChain,
        greeks,
        spot,
        capital,
        mode,
        atm_ce_premium: atmCePremium,
        atm_pe_premium: atmPePremium,
        time_to_expiry_mins: timeToExpiry,
        is_expiry: isExpiry,
        prev_close: parsedCandles[0]?.open || 0,
      });

      const data = result.data;
      setSignals(data.signals || []);
      setRegime(data.regime || null);
      setExpectedMove(data.expected_move || null);
      setIndicators(data.indicators || null);
      setMoveExhausted(data.move_exhausted || false);
      setLastScan(new Date());
      // Advanced state
      setDayType(data.day_type || null);
      setMoveUtilization(data.move_utilization || null);
      setTransition(data.transition || null);
      setTimePhase(data.time_phase || null);
      setDrawdown(data.drawdown || null);
      setProtectionZone(data.protection_zone || null);
      setScanMs(data.scan_ms || null);
    } catch (err) {
      console.error('Algo scan error:', err);
      setError(err.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [capital, mode]);

  // Auto-scan: 5s during market hours, 60s after hours, single scan on weekends
  useEffect(() => {
    if (enabled) {
      runScan(); // Initial scan
      const now = new Date();
      const day = now.getDay();
      const hour = now.getHours();
      const mins = now.getMinutes();
      const timeNum = hour * 60 + mins;
      const isWeekday = day >= 1 && day <= 5;
      const isDuringMarket = isWeekday && timeNum >= 555 && timeNum <= 930; // 9:15 - 15:30

      if (isDuringMarket) {
        intervalRef.current = setInterval(runScan, 5000); // 5s live
      } else if (isWeekday && timeNum > 930 && timeNum < 960) {
        intervalRef.current = setInterval(runScan, 30000); // 30s for post-close window
      }
      // Off-hours/weekends: no repeat — single scan with last trading day data
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, runScan]);

  // Record trade result for adaptive learning
  const recordResult = useCallback(async (tradeData) => {
    try {
      const result = await algoRecordResult(tradeData);
      return result.data;
    } catch (err) {
      console.error('Record result error:', err);
      return null;
    }
  }, []);

  // Reset daily state
  const resetDaily = useCallback(async () => {
    try {
      await algoResetDaily();
      setSignals([]);
    } catch (err) {
      console.error('Reset daily error:', err);
    }
  }, []);

  // Update settings
  const updateSettings = useCallback(async (newSettings) => {
    try {
      const result = await algoUpdateSettings(newSettings);
      return result.data;
    } catch (err) {
      console.error('Update settings error:', err);
      return null;
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const result = await algoStats();
      setStats(result.data);
      return result.data;
    } catch (err) {
      console.error('Fetch stats error:', err);
      return null;
    }
  }, []);

  // Monte Carlo simulation
  const runMonteCarlo = useCallback(async (params) => {
    try {
      const result = await algoMonteCarlo(params);
      return result.data;
    } catch (err) {
      console.error('Monte Carlo error:', err);
      return null;
    }
  }, []);

  // System health check
  const fetchSystemHealth = useCallback(async () => {
    try {
      const result = await algoSystemHealth();
      return result.data;
    } catch (err) {
      console.error('System health error:', err);
      return null;
    }
  }, []);

  // Performance distribution
  const fetchPerformance = useCallback(async () => {
    try {
      const result = await algoPerformance();
      return result.data;
    } catch (err) {
      console.error('Performance error:', err);
      return null;
    }
  }, []);

  return {
    signals,
    regime,
    expectedMove,
    indicators,
    moveExhausted,
    scanning,
    error,
    lastScan,
    stats,
    marketOpen,
    // Advanced state
    dayType,
    moveUtilization,
    transition,
    timePhase,
    drawdown,
    protectionZone,
    scanMs,
    // Actions
    runScan,
    recordResult,
    resetDaily,
    updateSettings,
    fetchStats,
    runMonteCarlo,
    fetchSystemHealth,
    fetchPerformance,
  };
}

// Helper: Determine candle date range — uses last trading day if market is closed
function getCandleDateRange(now) {
  const day = now.getDay(); // 0=Sun, 6=Sat
  const hour = now.getHours();
  const mins = now.getMinutes();
  const timeNum = hour * 60 + mins; // minutes since midnight

  const isWeekday = day >= 1 && day <= 5;
  const marketOpenTime = 555;  // 9:15 AM
  const marketCloseTime = 930; // 3:30 PM

  // Determine if market is currently live
  const isLive = isWeekday && timeNum >= marketOpenTime && timeNum <= marketCloseTime;

  // Determine the trading date to fetch candles for
  let tradingDate = new Date(now);

  if (day === 0) {
    // Sunday → use Friday
    tradingDate.setDate(tradingDate.getDate() - 2);
  } else if (day === 6) {
    // Saturday → use Friday
    tradingDate.setDate(tradingDate.getDate() - 1);
  } else if (isWeekday && timeNum < marketOpenTime) {
    // Before market open → use previous trading day
    if (day === 1) {
      // Monday before open → use Friday
      tradingDate.setDate(tradingDate.getDate() - 3);
    } else {
      tradingDate.setDate(tradingDate.getDate() - 1);
    }
  }
  // else: weekday during/after market → use today

  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const dateStr = fmt(tradingDate);

  // From: market open, To: current time (if live) or market close (if after hours)
  const fromDate = `${dateStr} 09:15`;
  let toDate;

  if (isLive) {
    toDate = `${dateStr} ${String(hour).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  } else {
    toDate = `${dateStr} 15:30`;
  }

  return { fromDate, toDate, isLive };
}

// Helper: Get next SENSEX expiry (Friday)
function getNextExpiry() {
  const now = new Date();
  const day = now.getDay();
  let daysUntilFriday = 5 - day;
  if (daysUntilFriday <= 0) daysUntilFriday += 7;
  // If it's Friday and market is still open, use today
  if (day === 5 && now.getHours() < 16) daysUntilFriday = 0;

  const expiry = new Date(now);
  expiry.setDate(now.getDate() + daysUntilFriday);

  const yyyy = expiry.getFullYear();
  const mm = String(expiry.getMonth() + 1).padStart(2, '0');
  const dd = String(expiry.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
